/**
 * Calculate cubic volume in m³ from dimensions
 * @param length - Length value
 * @param width - Width value
 * @param height - Height value
 * @param unit - Unit of measurement (cm, m, in, ft)
 * @returns Cubic volume in m³, or undefined if any dimension is missing
 */
export const calculateCubicVolume = (
  length: number | undefined,
  width: number | undefined,
  height: number | undefined,
  unit: "cm" | "m" | "in" | "ft" | undefined
): number | undefined => {
  // Return undefined if any dimension is missing
  if (!length || !width || !height || !unit) {
    return undefined;
  }

  // Ensure all values are positive
  if (length <= 0 || width <= 0 || height <= 0) {
    return undefined;
  }

  // Calculate volume in the given unit
  const volume = length * width * height;

  // Convert to m³ based on unit
  switch (unit) {
    case "cm":
      return volume / 1_000_000; // cm³ to m³
    case "m":
      return volume; // already in m³
    case "in":
      return volume / 61_024; // in³ to m³
    case "ft":
      return volume / 35.315; // ft³ to m³
    default:
      return undefined;
  }
};

/**
 * Format cubic volume for display
 * @param volume - Volume in m³
 * @returns Formatted string with 6 decimal places
 */
export const formatCubicVolume = (volume: number | undefined): string => {
  if (volume === undefined || volume === null) {
    return "—";
  }
  return `${volume.toFixed(6)} m³`;
};
